import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { format, parseISO, isWithinInterval } from "date-fns";
import { Printer, Eye, Save } from "lucide-react";
import { Patient, PatientService } from "@/types";
import { formatPatientId, generateBillNumber } from '@/lib/utils/patientId';
import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DialogFooter } from "@/components/ui/dialog";
import { Table, TableHeader, TableBody, TableCell, TableRow } from "@/components/ui/table";
import { useQueueStore } from "@/lib/store";
import { DateRangePicker } from "@/components/ui/date-range-picker";
import { DateRange } from "react-day-picker";
import { useHospitalProfileStore } from "@/lib/stores/hospitalProfile";
import { useBillsStore } from "@/lib/stores/bills";
import { usePatientsStore } from "@/lib/stores/patients";
import { useToast } from "@/components/ui/use-toast";

interface BillGenerationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  patient: Patient;
}

export function BillGenerationDialog({ isOpen, onClose, patient }: BillGenerationDialogProps) {
  const [discount, setDiscount] = useState(0);
  const [discountPercentage, setDiscountPercentage] = useState(0);
  const [isPercentage, setIsPercentage] = useState(false);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [isSaving, setIsSaving] = useState(false);
  const { patients } = useQueueStore();
  const { profile } = useHospitalProfileStore();
  const { createBill, bills, fetchBills } = useBillsStore();
  const { resetPatientSession } = usePatientsStore();
  const { toast } = useToast();
  
  // Fetch bills when dialog opens
  useEffect(() => {
    if (isOpen) {
      fetchBills();
    }
  }, [isOpen, fetchBills]);
  
  // Filter services and payments by date range
  const filteredServices = patient.services?.filter((service: PatientService) => {
    if (!dateRange?.from || !dateRange?.to) return true;
    const serviceDate = parseISO(service.added_at);
    return isWithinInterval(serviceDate, { start: dateRange.from, end: dateRange.to });
  }) || [];

  const filteredPayments = patient.paymentHistory?.filter(payment => {
    if (!dateRange?.from || !dateRange?.to) return true;
    const paymentDate = parseISO(payment.date);
    return isWithinInterval(paymentDate, { start: dateRange.from, end: dateRange.to });
  }) || [];

  // Calculate total cost with proper null checks
  const servicesCost = filteredServices.reduce((total, service) => {
    const price = Number(service.price) || 0;
    return total + price;
  }, 0);
  
  const totalCost = servicesCost;
  const amountPaid = Number(patient.amountPaid) || 0;
  
  // Calculate discount amount
  const discountAmount = isPercentage 
    ? (totalCost * discountPercentage) / 100 
    : discount;
  
  // Calculate final total after discount
  const finalTotal = totalCost - discountAmount;
  
  // Calculate remaining amount
  const remainingAmount = Math.max(0, finalTotal - amountPaid);

  // Get patient's bills
  const patientBills = bills.filter(bill => bill.patientId === patient.id);
  
  // Generate bill number
  const billNumber = generateBillNumber(patient.id, patientBills);

  const handlePrint = () => {
    // Create a new window for printing
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      // Fallback to regular print if popup is blocked
      window.print();
      return;
    }

    // Get the bill content
    const billContent = document.querySelector('[role="dialog"] .space-y-4');
    if (!billContent) {
      printWindow.close();
      return;
    }

    // Create the print HTML
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Bill - ${patient.name}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              margin: 0;
              padding: 10px 20px;
              font-size: 12px;
              line-height: 1.4;
              color: #000;
              background: white;
            }
            
            .bill-header {
              text-align: center;
              margin-bottom: 20px;
              padding-bottom: 10px;
              border-bottom: 2px solid #000;
            }
            
            .clinic-name {
              font-size: 18px;
              font-weight: bold;
              margin-bottom: 5px;
            }
            
            .clinic-details {
              font-size: 11px;
              color: #666;
              margin-bottom: 5px;
            }
            
            .patient-bill-info {
              display: flex;
              justify-content: space-between;
              margin-bottom: 20px;
              font-size: 11px;
            }
            
            .patient-info {
              flex: 1;
              margin-right: 20px;
            }
            
            .bill-info {
              flex: 1;
              text-align: right;
            }
            
            .patient-info > div {
              display: flex;
              gap: 5px;
              margin-bottom: 5px;
            }
            
            .bill-info > div {
              display: flex;
              justify-content: flex-end;
              gap: 5px;
              margin-bottom: 5px;
            }
            
            .label {
              font-weight: bold;
              color: #666;
            }
            
            .services-section {
              margin-bottom: 20px;
            }
            
            .service-header {
              display: grid;
              grid-template-columns: 2fr 1fr auto;
              gap: 15px;
              font-weight: bold;
              margin-bottom: 10px;
              padding-bottom: 5px;
              border-bottom: 1px solid #ccc;
            }
            
            .service-item {
              display: grid;
              grid-template-columns: 2fr 1fr auto;
              gap: 15px;
              margin-bottom: 5px;
              font-size: 11px;
            }
            
            .service-item > div:nth-child(2) {
              text-align: left;
              min-width: 80px;
            }
            
            .total-section {
              margin-top: 20px;
              border-top: 1px solid #ccc;
              padding-top: 10px;
            }
            
            .total-row {
              display: flex;
              justify-content: space-between;
              margin-bottom: 5px;
              font-size: 11px;
            }
            
            .total-row.final {
              font-weight: bold;
              font-size: 12px;
              border-top: 1px solid #000;
              padding-top: 5px;
              margin-top: 10px;
            }
            
            .payment-history {
              margin-top: 20px;
              border-top: 1px solid #ccc;
              padding-top: 10px;
            }
            
            .payment-item {
              display: flex;
              justify-content: space-between;
              margin-bottom: 5px;
              font-size: 11px;
            }
            
            .footer {
              text-align: center;
              margin-top: 30px;
              padding-top: 10px;
              border-top: 1px solid #ccc;
              font-size: 10px;
              color: #666;
            }
            
            .amount {
              text-align: right;
            }
            
            .due-amount {
              color: #dc2626;
              font-weight: bold;
            }
            
            .fully-paid {
              color: #16a34a;
              font-weight: bold;
            }
            
            @media print {
              body {
                margin: 0;
                padding: 15px;
              }
              
              .bill-header {
                margin-bottom: 15px;
              }
              
              .patient-info {
                margin-bottom: 15px;
              }
              
              .services-section {
                margin-bottom: 15px;
              }
            }
          </style>
        </head>
        <body>
          <div class="bill-header">
            <div class="clinic-name">${profile.name}</div>
            <div class="clinic-details">${profile.address}</div>
            <div class="clinic-details">Phone: ${profile.phone1} | Email: ${profile.email}</div>
            ${profile.website ? `<div class="clinic-details">Website: ${profile.website}</div>` : ''}
          </div>
          
          <div class="patient-bill-info">
            <div class="patient-info">
              <div><span class="label">Name:</span> ${patient.name}</div>
              <div><span class="label">Age/Gender:</span> ${patient.age} / ${patient.gender}</div>
              <div><span class="label">Contact:</span> ${patient.contactNumber}</div>
              <div><span class="label">Patient ID:</span> ${formatPatientId(patient.id)}</div>
            </div>
            <div class="bill-info">
              <div><span class="label">Date:</span> ${format(new Date(), "dd MMM yyyy")}</div>
              <div><span class="label">Bill No:</span> ${billNumber}</div>
            </div>
          </div>
          
          <div class="services-section">
            <div class="service-header">
              <div>Service</div>
              <div></div>
              <div class="amount">Amount (₹)</div>
            </div>
            ${filteredServices.map((service) => `
              <div class="service-item">
                <div>${service.service_name}</div>
                <div>${format(new Date(service.added_at), 'dd MMM yyyy')}</div>
                <div class="amount">${(Number(service.price) || 0).toFixed(2)}</div>
              </div>
            `).join('')}
          </div>
          
          <div class="total-section">
            <div class="total-row">
              <span>Total Amount</span>
              <span class="amount">₹${totalCost.toFixed(2)}</span>
            </div>
            ${discountAmount > 0 ? `
              <div class="total-row">
                <span>Discount</span>
                <span class="amount">-₹${discountAmount.toFixed(2)}</span>
              </div>
            ` : ''}
            <div class="total-row final">
              <span>Final Total</span>
              <span class="amount">₹${finalTotal.toFixed(2)}</span>
            </div>
            <div class="total-row">
              <span>Amount Paid</span>
              <span class="amount">₹${amountPaid.toFixed(2)}</span>
            </div>
            ${patient.paymentMode ? `
              <div class="total-row">
                <span>Payment Mode</span>
                <span class="amount">${patient.paymentMode}</span>
              </div>
            ` : ''}
            <div class="total-row final ${remainingAmount > 0 ? 'due-amount' : 'fully-paid'}">
              <span>${remainingAmount > 0 ? 'Due Amount' : 'Fully Paid'}</span>
              <span class="amount">₹${Math.abs(remainingAmount).toFixed(2)}</span>
            </div>
          </div>
          
          ${filteredPayments.length > 0 ? `
            <div class="payment-history">
              <div style="font-weight: bold; margin-bottom: 10px;">Payment History</div>
              ${filteredPayments.map((payment, index) => `
                <div class="payment-item">
                  <div>
                    <strong>₹${(Number(payment.amount) || 0).toFixed(2)}</strong>
                    <span style="color: #666; margin-left: 10px;">
                      (${format(new Date(payment.date), 'dd MMM yyyy, HH:mm')})
                    </span>
                  </div>
                  <span style="text-transform: capitalize;">${payment.mode}</span>
                </div>
              `).join('')}
            </div>
          ` : ''}
          
          <div class="footer">
            <p>Thank you for choosing ${profile.name}</p>
            <p>This is a computer generated receipt</p>
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();
    
    // Wait for content to load then print
    printWindow.onload = () => {
      printWindow.print();
      printWindow.close();
    };
  };

  const handlePreview = () => {
    setIsPreviewMode(true);
  };

  const handleClosePreview = () => {
    setIsPreviewMode(false);
  };

  const handleSaveBill = async () => {
    setIsSaving(true);
    try {
      // Create bill data
      const billData = {
        patientId: patient.id,
        billNumber,
        billDate: new Date().toISOString(),
        services: filteredServices,
        totalAmount: totalCost,
        amountPaid: amountPaid,
        paymentMode: patient.paymentMode || 'cash',
        discountAmount: discountAmount,
        discountPercentage: discountPercentage,
        clinicDetails: {
          name: profile.name,
          address: profile.address,
          phone: profile.phone1,
          email: profile.email,
          website: profile.website,
        },
        status: 'active' as const,
      };

      // Save bill to database
      const savedBill = await createBill(billData);
      
      if (savedBill) {
        // Reset patient session for new consultation
        await resetPatientSession(patient.id);
        
        toast({
          title: "Bill saved successfully",
          description: `Bill ${billNumber} has been saved and patient session reset for new consultation.`,
        });
        
        onClose();
      } else {
        throw new Error('Failed to save bill');
      }
    } catch (error) {
      console.error('Error saving bill:', error);
      toast({
        title: "Error saving bill",
        description: error instanceof Error ? error.message : 'Failed to save bill',
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={`max-w-4xl max-h-[90vh] overflow-y-auto ${isPreviewMode ? 'print-preview' : ''}`}>
        <DialogHeader>
          <div className="flex items-center justify-between w-full">
            <div>
              <DialogTitle>Bill Generation</DialogTitle>
              <DialogDescription>
                Review and generate the bill for {patient.name}
              </DialogDescription>
            </div>
            <div className="ml-4">
              <DateRangePicker
                value={dateRange}
                onChange={setDateRange}
                className="w-48 h-9 text-sm"
              />
            </div>
          </div>
        </DialogHeader>
        
        <div className="space-y-4 p-4">
          {/* Header */}
          <div className="flex justify-between items-start border-b pb-2">
            <div>
              <h2 className="text-lg font-bold">{profile.name}</h2>
              <p className="text-sm text-muted-foreground">{profile.address}</p>
              <p className="text-sm text-muted-foreground">Phone: {profile.phone1}</p>
              <p className="text-sm text-muted-foreground">Email: {profile.email}</p>
              <p className="text-sm text-muted-foreground">Website: {profile.website}</p>
            </div>
            <div className="text-right">
              <p className="text-sm font-medium">Date: {format(new Date(), "dd MMM yyyy")}</p>
              <p className="text-sm font-medium">Bill No: {billNumber}</p>
            </div>
          </div>

          {/* Patient Info */}
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div>
              <span className="text-muted-foreground">Name:</span>
              <span className="ml-1 font-medium">{patient.name}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Age/Gender:</span>
              <span className="ml-1 font-medium">{patient.age} / {patient.gender}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Contact:</span>
              <span className="ml-1 font-medium">{patient.contactNumber}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Patient ID:</span>
              <span className="ml-1 font-medium">{formatPatientId(patient.id)}</span>
            </div>
          </div>

          {/* Services and Charges */}
          <div className="space-y-2">
            <div className="grid grid-cols-3 gap-2 text-sm">
              <div className="font-medium">Service</div>
              <div className="font-medium">Date</div>
              <div className="font-medium text-right">Amount (₹)</div>
            </div>
            
            {/* Services */}
            {filteredServices.length > 0 && (
              <div className="space-y-2">
                {filteredServices.map((service) => (
                  <div key={service.id} className="grid grid-cols-3 gap-2 text-sm">
                    <div>{service.service_name}</div>
                    <div>{format(new Date(service.added_at), 'dd MMM yyyy')}</div>
                    <div className="text-right">{(Number(service.price) || 0).toFixed(2)}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Payment Summary */}
            <div className="space-y-1">
              {/* Total Amount */}
              <div className="grid grid-cols-2 gap-2 text-sm font-medium border-t pt-1">
                <div>Total Amount</div>
                <div className="text-right">{totalCost.toFixed(2)}</div>
              </div>

              {/* Discount Section */}
              <div className="border-t pt-2 space-y-2">
                <div className="flex items-center gap-2">
                  <Checkbox 
                    id="percentage" 
                    checked={isPercentage}
                    onCheckedChange={(checked) => setIsPercentage(checked === true)}
                  />
                  <Label htmlFor="percentage">Apply percentage discount</Label>
                  {isPercentage ? (
                    <div className="flex items-center gap-2 ml-2">
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={discountPercentage}
                        onChange={(e) => setDiscountPercentage(Number(e.target.value))}
                        className="w-20"
                      />
                      <span>%</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 ml-2">
                      <span>₹</span>
                      <Input
                        type="number"
                        min="0"
                        max={totalCost}
                        value={discount}
                        onChange={(e) => setDiscount(Number(e.target.value))}
                        className="w-24"
                      />
                    </div>
                  )}
                </div>
                
                <div className="flex justify-between text-muted-foreground">
                  <span>Discount Amount:</span>
                  <span className="discount-amount">-₹{discountAmount.toFixed(2)}</span>
                </div>
              </div>
              
              <div className="flex justify-between font-medium">
                <span>Final Total:</span>
                <span>₹{finalTotal.toFixed(2)}</span>
              </div>

              {/* Amount Paid */}
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>Amount Paid</div>
                <div className="text-right">{amountPaid.toFixed(2)}</div>
              </div>

              {/* Payment Mode */}
              {patient.paymentMode && (
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>Payment Mode</div>
                  <div className="text-right capitalize">{patient.paymentMode}</div>
                </div>
              )}

              {/* Amount Due */}
              <div className="grid grid-cols-2 gap-2 text-sm font-medium border-t pt-1">
                <div className={remainingAmount > 0 ? "text-red-600" : "text-green-600"}>
                  {remainingAmount > 0 ? "Due Amount" : "Fully Paid"}
                </div>
                <div className={`text-right ${remainingAmount > 0 ? "text-red-600" : "text-green-600"}`}>
                  {Math.abs(remainingAmount).toFixed(2)}
                </div>
              </div>
            </div>
          </div>

          {/* Payment History */}
          {filteredPayments.length > 0 && (
            <div className="space-y-2">
              <div className="font-medium">Payment History</div>
              {filteredPayments.map((payment, index) => (
                <div key={index} className="flex justify-between text-sm">
                  <div>
                    <span className="font-medium">₹{(Number(payment.amount) || 0).toFixed(2)}</span>
                    <span className="text-muted-foreground ml-2">
                      ({format(new Date(payment.date), 'dd MMM yyyy, HH:mm')})
                    </span>
                  </div>
                  <span className="capitalize">{payment.mode}</span>
                </div>
              ))}
            </div>
          )}

          {/* Footer */}
          <div className="text-center text-xs text-muted-foreground border-t pt-2">
            <p>Thank you for choosing {profile.name}</p>
            <p>This is a computer generated receipt</p>
          </div>
        </div>

        {/* Print, Preview, and Save Buttons */}
        <div className="flex justify-end gap-2">
          {isPreviewMode ? (
            <Button onClick={handleClosePreview} variant="outline">
              Close Preview
            </Button>
          ) : (
            <Button onClick={handlePreview} variant="outline">
              <Eye className="mr-2 h-4 w-4" />
              Preview
            </Button>
          )}
          <Button onClick={handlePrint} variant="outline">
            <Printer className="mr-2 h-4 w-4" />
            Print Receipt
          </Button>
          <Button 
            onClick={handleSaveBill} 
            disabled={isSaving}
            className="bg-green-600 hover:bg-green-700"
          >
            <Save className="mr-2 h-4 w-4" />
            {isSaving ? "Saving..." : "Save Bill"}
          </Button>
        </div>

        <style>{`
          .print-preview {
            background-color: white;
            box-shadow: 0 0 0 100vmax rgba(0, 0, 0, 0.5);
            margin: 2rem auto;
            max-width: 21cm;
            padding: 2cm;
          }

          .print-preview .dialog-header,
          .print-preview .dialog-description {
            display: none;
          }

          .print-preview button[onclick*="handlePrint"] {
            display: none;
          }
        `}</style>
      </DialogContent>
    </Dialog>
  );
} 